import { Grid, GridProps, IconButton, TextField } from '@material-ui/core';
import { TextFieldProps } from '@material-ui/core/TextField';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React, { ReactNode, useEffect, useState } from 'react';
import { Plus as AddIcon } from 'react-feather';
import { useDebouncedCallback } from 'use-debounce';
import { JupiterTheme } from '../../theme/types';
import CloseButton from '../buttons/CloseButton';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    addIcon: {
      height: 24,
      strokeWidth: 1.25,
      width: 24,
    },
    label: {
      backgroundColor: 'none',
      background: 'none',
    },
    addButton: {
      background: theme.palette.navy[800],
      color: theme.palette.coolGrey[50],
      '&:hover': {
        background: theme.palette.primary.dark,
      },
      marginTop: theme.spacing(1),
    },
    addContainer: {
      display: 'flex',
      flexDirection: 'row-reverse',
      marginTop: theme.spacing(1),
    },
    remove: {
      marginLeft: theme.spacing(-1),
    },
    tooltip: {
      background: theme.palette.background.paper,
      color: theme.palette.navy[800],
      padding: theme.spacing(1.5),
      fontWeight: 'normal',
    },
    iconButton: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1),
    },
    helperText: { color: theme.palette.red[500] },
    container: {
      border: `1px solid ${theme.palette.coolGrey[500]}`,
      padding: theme.spacing(2),
    },
    error: {
      border: `1px solid ${theme.palette.red[500]}`,
    },
  })
);
interface Value {
  [key: string]: string;
}
interface FlattenedValue extends Value {
  key: string;
  value: string;
}

interface Props {
  disabled?: boolean;
  initialValues: Value;
  onChange: (newValues: Value) => void;
  label?: ReactNode;
  keyInputProps?: TextFieldProps;
  valueInputProps?: TextFieldProps;
  error?: boolean;
  helperText?: string;
  keyGridItemProps?: GridProps;
  valueGridItemProps?: GridProps;
  classes?: {
    root?: string;
    addButton?: string;
  };
}

export const KeyValueInput = ({
  disabled,
  initialValues,
  onChange,
  label,
  keyInputProps,
  valueInputProps,
  error,
  helperText,
  keyGridItemProps,
  valueGridItemProps,
  ...props
}: Props) => {
  const classes = useStyles();
  const [flattenedValues, setFlattenedValues] = useState<FlattenedValue[]>(
    Object.keys(initialValues || {}).map((o) => ({
      key: o,
      value: initialValues[o],
    }))
  );

  const [debouncedOnChange] = useDebouncedCallback(
    () =>
      onChange(
        flattenedValues.reduce(
          (acc: Value, v: FlattenedValue) => ({ ...acc, [v.key]: v.value }),
          {}
        )
      ),
    500
  );

  useEffect(debouncedOnChange, [flattenedValues]);

  const updateValue = (i: number, attributeToUpdate: string) => (e: any) => {
    const newFlattenedValues = [...flattenedValues];
    newFlattenedValues[i][attributeToUpdate] = e.target.value;
    setFlattenedValues(newFlattenedValues);
  };

  const handleAdd = () => {
    const newFlattenedValues = [...flattenedValues, { key: '', value: '' }];
    setFlattenedValues(newFlattenedValues);
  };

  const handleRemove = (i: number) => () => {
    const newFlattenedValues = [...flattenedValues];
    newFlattenedValues.splice(i, 1);
    setFlattenedValues(newFlattenedValues);
  };

  return (
    <>
      {label}
      <div
        className={clsx(
          error && classes.error,
          classes.container,
          props.classes?.root
        )}
      >
        <Grid container={true} spacing={2}>
          {flattenedValues.map(
            ({ key, value }: FlattenedValue, index: number) => {
              return (
                <React.Fragment key={index}>
                  <Grid item xs={12} md={3} {...keyGridItemProps}>
                    <TextField
                      name={key}
                      label={index === 0 ? 'Key' : ''}
                      fullWidth
                      disabled={disabled}
                      onChange={updateValue(index, 'key')}
                      value={key}
                      {...keyInputProps}
                    />
                  </Grid>
                  <Grid item xs={11} md={8} {...valueGridItemProps}>
                    <TextField
                      name={`${key}-value`}
                      fullWidth
                      label={index === 0 ? 'Value' : ''}
                      disabled={disabled}
                      onChange={updateValue(index, 'value')}
                      value={value}
                      {...valueInputProps}
                    />
                  </Grid>
                  {!disabled && (
                    <Grid item={true} xs={1}>
                      <CloseButton
                        className={classes.remove}
                        label="Remove Item"
                        size={20}
                        onClose={handleRemove(index)}
                      />
                    </Grid>
                  )}
                </React.Fragment>
              );
            }
          )}
        </Grid>
        {!disabled && (
          <div className={classes.addContainer}>
            <IconButton
              id="add-item"
              size="small"
              className={clsx(classes.addButton, props.classes?.addButton)}
              onClick={handleAdd}
            >
              <AddIcon className={classes.addIcon} />
            </IconButton>
          </div>
        )}
      </div>
      <p className={classes.helperText}>{helperText}</p>
    </>
  );
};

export default KeyValueInput;
